<script lang="ts">
	// Create an account with an email and a password (D17, UC-25).
	//
	// Nothing is made here. The server mails the address a link, and the
	// account starts when that link is opened (D18). Its answer is the same
	// for every address, so "Check your email" is all this screen ever says:
	// a new address, one that already has an account, and one past the
	// hour's mail look alike, and only the mailbox learns which.

	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Band from '../components/Band.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import { passwordLengthOk, signUp, type SignUpForm } from '../owner/password';
	import { navigate } from '../router.svelte';

	interface Props {
		language: Language;
	}

	let { language }: Props = $props();

	// How long before the mail may be asked for again, in seconds. The server
	// caps it anyway; this keeps a hurried second press from spending a mail.
	const RESEND_AFTER = 60;

	let name = $state('');
	let email = $state('');
	let password = $state('');
	// The mail comes in the language the screen is in, unless the owner picks
	// another: no restaurant exists yet to say which.
	let picked = $state<Language | null>(null);
	const mailLanguage = $derived<Language>(picked ?? language);

	let working = $state(false);
	let errors = $state<{ name?: string; email?: string; password?: string }>({});
	let failure = $state('');

	// The form as it was sent, kept so Send it again sends the same one.
	let sent = $state<SignUpForm | null>(null);
	let left = $state(0);
	let resending = $state(false);
	let resent = $state(false);
	let timer: ReturnType<typeof setInterval> | undefined;

	const ready = $derived(name.trim().length > 0 && email.trim().length > 0 && password.length > 0);

	const languageOptions = $derived([
		{
			value: 'ar',
			label: message(language, 'settingsLanguageArabic'),
			testId: 'create-account-language-ar',
		},
		{
			value: 'msa',
			label: message(language, 'settingsLanguageMSA'),
			testId: 'create-account-language-msa',
		},
		{
			value: 'en',
			label: message(language, 'settingsLanguageEnglish'),
			testId: 'create-account-language-en',
		},
	]);

	function countDown() {
		left = RESEND_AFTER;
		clearInterval(timer);
		timer = setInterval(() => {
			left = Math.max(0, left - 1);
			if (left === 0) clearInterval(timer);
		}, 1000);
	}

	$effect(() => () => clearInterval(timer));

	function minutesAndSeconds(seconds: number): string {
		return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
	}

	async function send(form: SignUpForm): Promise<boolean> {
		const result = await signUp(form);
		switch (result.kind) {
			case 'sent':
				return true;
			case 'weak':
				errors = { password: message(language, 'passwordRule') };
				return false;
			case 'invalid':
				errors =
					result.field === 'email'
						? { email: message(language, 'createAccountBadEmail') }
						: result.field === 'name'
							? { name: result.message }
							: {};
				if (result.field !== 'email' && result.field !== 'name') failure = result.message;
				return false;
			default:
				failure = message(language, 'loadFailed');
				return false;
		}
	}

	async function create() {
		if (!ready || working) return;
		errors = {};
		failure = '';
		// Checked here too, so a short password is named before anything is sent.
		if (!passwordLengthOk(password)) {
			errors = { password: message(language, 'passwordRule') };
			return;
		}
		working = true;
		const form = { name, email, password, language: mailLanguage };
		const ok = await send(form);
		working = false;
		if (!ok) return;
		sent = { ...form, email: email.trim() };
		resent = false;
		countDown();
	}

	async function sendAgain() {
		if (!sent || left > 0 || resending) return;
		resending = true;
		const ok = await send(sent);
		resending = false;
		if (!ok) return;
		resent = true;
		countDown();
	}
</script>

<section class="screen" data-testid="screen-create-account">
	<div class="card">
		{#if sent === null}
			<div class="words">
				<h1>{message(language, 'createAccountTitle')}</h1>
				<p class="instruction">{message(language, 'createAccountInstruction')}</p>
			</div>
			{#if failure}
				<Band tone="flag" testId="create-account-failure">{failure}</Band>
			{/if}
			<Field
				testId="create-account-name"
				label={message(language, 'createAccountName')}
				autocomplete="name"
				maxLength={100}
				value={name}
				error={errors.name}
				onInput={(value) => (name = value)}
			/>
			<Field
				testId="create-account-email"
				label={message(language, 'signInEmail')}
				type="email"
				autocomplete="email"
				mono
				maxLength={254}
				value={email}
				error={errors.email}
				errorTestId="create-account-email-error"
				onInput={(value) => (email = value)}
			/>
			<Field
				testId="create-account-password"
				label={message(language, 'signInPassword')}
				type="password"
				autocomplete="new-password"
				mono
				maxLength={128}
				value={password}
				note={message(language, 'passwordRule')}
				error={errors.password}
				errorTestId="create-account-password-error"
				reveal={{
					show: message(language, 'passwordShow'),
					hide: message(language, 'passwordHide'),
					testId: 'create-account-password-show',
				}}
				onInput={(value) => (password = value)}
			/>
			<div class="pick">
				<span class="pick-label">{message(language, 'createAccountLanguage')}</span>
				<Segmented
					testId="create-account-language"
					label={message(language, 'createAccountLanguage')}
					options={languageOptions}
					value={mailLanguage}
					onChange={(value) => (picked = value as Language)}
				/>
			</div>
			<Button
				testId="create-account-submit"
				label={message(language, 'createAccountSubmit')}
				wide
				disabled={!ready}
				state={working ? 'pending' : 'idle'}
				pendingLabel={message(language, 'createAccountCreating')}
				onPress={() => void create()}
			/>
		{:else}
			<div class="words" data-testid="create-account-sent">
				<h1>{message(language, 'checkEmailTitle')}</h1>
				<p class="instruction">
					{message(language, 'checkEmailBefore')}
					<bdi class="address" dir="ltr" data-testid="create-account-sent-to">{sent.email}</bdi
					>{message(language, 'checkEmailAfter')}
				</p>
			</div>
			<div class="again">
				<Button
					testId="create-account-send-again"
					label={message(language, 'sendAgain')}
					tone="secondary"
					size="md"
					disabled={left > 0}
					state={resending ? 'pending' : 'idle'}
					pendingLabel={message(language, 'sending')}
					onPress={() => void sendAgain()}
				/>
				{#if resent}
					<span class="resent" role="status">{message(language, 'sentAgain')}</span>
				{/if}
				{#if left > 0}
					<span class="wait">{fill(message(language, 'askAgainIn'), minutesAndSeconds(left))}</span>
				{/if}
			</div>
		{/if}
		<div class="foot">
			<Button
				testId="create-account-back"
				label={message(language, 'backToSignIn')}
				tone="text-quiet"
				size="sm"
				onPress={() => navigate('/owner')}
			/>
		</div>
	</div>
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
	}

	.card {
		inline-size: 100%;
		max-inline-size: 480px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.words {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	.instruction {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.address {
		font-family: var(--font-mono);
		font-weight: 600;
		color: var(--text-strong);
	}

	.pick {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.pick-label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.again {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-4);
	}

	.resent {
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
	}

	.wait {
		font-size: var(--text-sm);
		color: var(--text-muted);
		white-space: nowrap;
	}

	.foot {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2);
		padding-block-start: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-inset);
	}
</style>
